import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Plus, CalendarIcon, MapPin, Video, Users, Clock, Loader2,
  Download, CheckCircle, XCircle, AlertCircle, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EventsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_date: new Date(),
    end_date: new Date(),
    location: '',
    is_virtual: false,
    meeting_link: '',
  });

  const fetchEvents = useCallback(async () => {
    try {
      const [eventsRes, regsRes] = await Promise.all([
        axios.get(`${API}/events/`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/events/my-registrations/all`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setEvents(eventsRes.data);
      setMyRegistrations(regsRes.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim() || !newEvent.description.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/events/`, newEvent, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Event created!');
      setShowCreateDialog(false);
      setNewEvent({
        title: '', description: '', start_date: new Date(), end_date: new Date(),
        location: '', is_virtual: false, meeting_link: '',
      });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleRegister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/register`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Registration submitted! Awaiting admin approval.');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/cancel-attendance`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Registration cancelled');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const getRegistrationStatus = (eventId) => {
    const reg = myRegistrations.find(r => r.event_id === eventId);
    return reg ? reg.status : null;
  };

  const downloadICS = async (event) => {
    try {
      const response = await axios.get(`${API}/events/${event.event_id}/calendar.ics`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.title}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Calendar file downloaded!');
    } catch {
      toast.error('Failed to download calendar file');
    }
  };

  const openGoogleCalendar = (event) => {
    const startDt = new Date(event.start_date);
    const endDt = new Date(event.end_date);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const loc = event.is_virtual ? (event.meeting_link || 'Online') : (event.location || '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${fmt(startDt)}/${fmt(endDt)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(loc)}`;
    window.open(url, '_blank');
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-500/10 text-green-500 border-green-500/20" data-testid="status-approved"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive" data-testid="status-rejected"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    if (status === 'pending') return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20" data-testid="status-pending"><AlertCircle className="h-3 w-3 mr-1" />Pending Approval</Badge>;
    return null;
  };

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="events-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">Events</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Discover upcoming webinars, workshops, and community meetups
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-event-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Create New Event</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="event-title">Event Title</Label>
                  <Input
                    id="event-title"
                    placeholder="e.g., H1B Filing Workshop"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent((prev) => ({ ...prev, title: e.target.value }))}
                    className="input-default"
                    data-testid="event-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-desc">Description</Label>
                  <Textarea
                    id="event-desc"
                    placeholder="What's this event about?"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent((prev) => ({ ...prev, description: e.target.value }))}
                    className="input-default min-h-[80px]"
                    data-testid="event-desc-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left text-sm">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(newEvent.start_date, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newEvent.start_date}
                          onSelect={(date) => setNewEvent((prev) => ({ ...prev, start_date: date || new Date() }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left text-sm">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(newEvent.end_date, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newEvent.end_date}
                          onSelect={(date) => setNewEvent((prev) => ({ ...prev, end_date: date || new Date() }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="event-virtual"
                    checked={newEvent.is_virtual}
                    onCheckedChange={(checked) => setNewEvent((prev) => ({ ...prev, is_virtual: checked }))}
                    data-testid="event-virtual-toggle"
                  />
                  <Label htmlFor="event-virtual">Virtual Event</Label>
                </div>
                {newEvent.is_virtual ? (
                  <div className="space-y-2">
                    <Label htmlFor="meeting-link">Meeting Link</Label>
                    <Input
                      id="meeting-link"
                      placeholder="https://zoom.us/..."
                      value={newEvent.meeting_link}
                      onChange={(e) => setNewEvent((prev) => ({ ...prev, meeting_link: e.target.value }))}
                      className="input-default"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      placeholder="e.g., San Francisco, CA"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent((prev) => ({ ...prev, location: e.target.value }))}
                      className="input-default"
                    />
                  </div>
                )}
                <Button
                  onClick={handleCreateEvent}
                  disabled={creating || !newEvent.title.trim() || !newEvent.description.trim()}
                  className="w-full btn-primary"
                  data-testid="submit-event-btn"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Event'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <Card className="card-default text-center py-12">
            <p className="text-muted-foreground">No upcoming events. Create one to get started!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {events.map((event, index) => {
              const regStatus = getRegistrationStatus(event.event_id);
              return (
                <Card
                  key={event.event_id}
                  className="card-default overflow-hidden animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`event-${event.event_id}`}
                >
                  {event.cover_image && (
                    <div className="h-36 sm:h-40 bg-muted">
                      <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Badge variant={event.is_virtual ? 'default' : 'secondary'} className="mb-2">
                          {event.is_virtual ? <Video className="h-3 w-3 mr-1" /> : <MapPin className="h-3 w-3 mr-1" />}
                          {event.is_virtual ? 'Virtual' : 'In-Person'}
                        </Badge>
                        <CardTitle className="text-base sm:text-lg leading-tight">{event.title}</CardTitle>
                      </div>
                    </div>
                    <CardDescription className="line-clamp-2 text-sm">{event.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{formatEventDate(event.start_date)}</span>
                      </div>
                      {!event.is_virtual && event.location && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-3.5 w-3.5 flex-shrink-0" />
                        {event.attendee_count} registered
                      </div>
                    </div>

                    {/* Registration status */}
                    {regStatus && (
                      <div data-testid={`reg-status-${event.event_id}`}>
                        {statusBadge(regStatus)}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2">
                      {!regStatus ? (
                        <Button
                          className="w-full btn-primary"
                          onClick={() => handleRegister(event.event_id)}
                          data-testid={`register-event-${event.event_id}`}
                        >
                          Register
                        </Button>
                      ) : regStatus === 'approved' ? (
                        <>
                          {/* Calendar download options */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button className="w-full btn-primary" data-testid={`add-to-calendar-${event.event_id}`}>
                                <Download className="h-4 w-4 mr-2" />
                                Add to Calendar
                                <ChevronDown className="h-4 w-4 ml-2" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center" className="w-48">
                              <DropdownMenuItem onClick={() => openGoogleCalendar(event)} data-testid={`cal-google-${event.event_id}`}>
                                Google Calendar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => downloadICS(event)} data-testid={`cal-ics-${event.event_id}`}>
                                Outlook / Apple (.ics)
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full text-destructive"
                            onClick={() => handleCancelRegistration(event.event_id)}
                            data-testid={`cancel-reg-${event.event_id}`}
                          >
                            Cancel Registration
                          </Button>
                        </>
                      ) : regStatus === 'pending' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-muted-foreground"
                          onClick={() => handleCancelRegistration(event.event_id)}
                          data-testid={`cancel-pending-${event.event_id}`}
                        >
                          Cancel Registration
                        </Button>
                      ) : (
                        <Button
                          className="w-full btn-primary"
                          onClick={() => handleRegister(event.event_id)}
                          data-testid={`re-register-${event.event_id}`}
                        >
                          Register Again
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
